import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box, Button, Flex, HStack, Icon, IconButton, Input, InputGroup,
  InputLeftElement, Skeleton, Text, Tooltip, VStack,
} from "@chakra-ui/react";
import {
  HiOutlineArrowPath, HiOutlineArrowTopRightOnSquare, HiOutlineChartBar,
  HiOutlineClipboardDocumentList, HiOutlineEnvelope, HiOutlineLockClosed,
  HiOutlineMagnifyingGlass,
} from "react-icons/hi2";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Header from "../Header";
import { logout } from "../../Components/auth/logout";
import { BASE_API_URL } from "../../Components/config";
import { buildMatcher, haystack } from "../../Components/searchMatch";
import { c, useClassroomColorMode } from "../classroomColors";
import { brand } from "../standeTokens";
import { useClassroomToast } from "../classroomToast";
import { STAGES, formatDateTime } from "../marking/markingTokens";
import "../classroom-theme.css";

/**
 * The reviewers' queue for free practice papers.
 *
 * Free paper sign-ups are deliberately kept out of the classroom experience,
 * so this is the one screen that replaces a class roster: who has submitted,
 * who is still working, and who has had their feedback released.
 *
 * Marking and report editing are not rebuilt here — the rows link into the
 * existing screens for that. The actions this page owns are the last two
 * steps: releasing the feedback, and getting that email into the student's
 * inbox when the first send didn't land.
 */

const ENDPOINT = `${BASE_API_URL}/api/v1/practice-paper-requests`;

/**
 * Free paper statuses mapped onto the marking module's stages, so a dot means
 * the same thing here as it does on a classroom's marking tab.
 */
const STAGE_OF = {
  IN_PROGRESS: "not_submitted",
  AWAITING_REVIEW: "to_review",
  RELEASED: "released",
};

const STATUS_LABEL = {
  IN_PROGRESS: "Still working",
  AWAITING_REVIEW: "Awaiting review",
  RELEASED: "Released",
};

const LEAD_LABEL = {
  NOT_SIGNED_UP: "No account yet",
  SIGNED_UP_NOT_STARTED: "Signed up, not started",
  STARTED: "Started the paper",
};

/** The funnel runs left to right; the first step is the one worth chasing. */
const LEAD_FILTERS = [
  { key: "NOT_SIGNED_UP", label: "No account yet" },
  { key: "SIGNED_UP_NOT_STARTED", label: "Signed up, not started" },
  { key: "STARTED", label: "Started" },
  { key: "ALL", label: "All" },
];

const FILTERS = [
  { key: "AWAITING_REVIEW", label: "Awaiting review" },
  { key: "IN_PROGRESS", label: "Still working" },
  { key: "RELEASED", label: "Released" },
  { key: "ALL", label: "All" },
];

/** "3h ago" / "2d ago" since a timestamp, for the 24-hour promise. */
function waitingFor(isoDate) {
  if (!isoDate) return null;
  const then = new Date(isoDate.endsWith("Z") ? isoDate : `${isoDate}Z`);
  const hours = Math.floor((Date.now() - then.getTime()) / 36e5);
  if (Number.isNaN(hours)) return null;
  if (hours < 1) return "just now";
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

/** A headline number. Mirrors the tiles on the dashboard and marking panel. */
const Tile = ({ label, value, hint, tone }) => (
  <Box flex="1 1 130px" minW="130px">
    <Text fontSize="12px" color={c.textMuted} mb="5px">
      {label}
    </Text>
    <HStack spacing="6px" align="baseline">
      <Text fontSize="26px" fontWeight="500" lineHeight="1" color={tone || c.text}
        sx={{ fontVariantNumeric: "tabular-nums" }}>
        {value}
      </Text>
      {hint && (
        <Text fontSize="12.5px" fontWeight="300" color={c.textMuted}>
          {hint}
        </Text>
      )}
    </HStack>
  </Box>
);

const Card = ({ title, meta, children, ...rest }) => (
  <Box bg={c.surface} border="1px solid" borderColor={c.borderSoft}
    borderRadius="14px" p="18px" {...rest}>
    {(title || meta) && (
      <Flex justify="space-between" align="baseline" gap="12px" mb="14px" wrap="wrap">
        {title && (
          <Text fontSize="14px" fontWeight="600" color={c.text}>{title}</Text>
        )}
        {meta}
      </Flex>
    )}
    {children}
  </Box>
);

const Empty = ({ title, body }) => (
  <Flex direction="column" align="center" justify="center" py="56px" gap="9px"
    textAlign="center">
    <Flex boxSize="44px" borderRadius="full" bg={c.surfaceSubtle} align="center"
      justify="center">
      <Icon as={HiOutlineClipboardDocumentList} boxSize="19px" color={c.textMuted} />
    </Flex>
    <Text fontSize="15px" fontWeight="500" color={c.text}>{title}</Text>
    {body && (
      <Text fontSize="13px" fontWeight="300" color={c.textSub} maxW="340px">
        {body}
      </Text>
    )}
  </Flex>
);

/** Small outlined action, matching the icon buttons in the marking panel. */
const RowAction = ({ label, icon, onClick, isLoading, isDisabled }) => (
  <Tooltip label={label} placement="top" hasArrow fontSize="11px">
    <IconButton
      aria-label={label}
      icon={<Icon as={icon} boxSize="14px" />}
      size="sm" minW="30px" h="30px" borderRadius="7px" variant="outline"
      borderColor={c.border} color={c.textSub} bg="transparent"
      isLoading={isLoading} isDisabled={isDisabled}
      _hover={{ bg: c.hover, color: c.text, borderColor: c.textMuted }}
      onClick={onClick}
    />
  </Tooltip>
);

export default function FreePaperReview() {
  const { isDark, toggleColorMode } = useClassroomColorMode();
  const toast = useClassroomToast(isDark);
  const navigate = useNavigate();
  const b = brand(isDark);

  // null = still loading; [] = loaded, none.
  const [rows, setRows] = useState(null);
  const [leads, setLeads] = useState(null);
  const [filter, setFilter] = useState("AWAITING_REVIEW");
  const [leadFilter, setLeadFilter] = useState("NOT_SIGNED_UP");
  const [query, setQuery] = useState("");
  const [error, setError] = useState(null);
  // A 403 is not a failure to load, it is the wrong account. Kept apart so
  // the page can say so instead of rendering a dashboard full of zeroes.
  const [forbidden, setForbidden] = useState(false);
  const [busy, setBusy] = useState(null); // `${action}:${attemptGuid}`

  const authHeader = useMemo(() => {
    const token = localStorage.getItem("accessToken");
    const type = localStorage.getItem("tokenType") || "Bearer";
    return { Authorization: `${type} ${token}` };
  }, []);

  const handleLogout = () => logout({ navigate });

  /**
   * The whole queue, filtered in the browser.
   *
   * Asking the API per filter meant the counts could only ever describe the
   * tab you were on. One fetch serves the tiles, every chip and the search
   * at once. The API sorts waiting-on-us first and truncates the tail, so if
   * a campaign ever outgrows this limit it is the oldest released rows that
   * fall off, never the ones someone still has to act on.
   */
  const load = useCallback(async () => {
    setError(null);
    setForbidden(false);
    try {
      const [queue, funnel] = await Promise.all([
        axios.get(`${ENDPOINT}/review-queue`, {
          headers: authHeader, params: { limit: 500 },
        }),
        axios.get(`${ENDPOINT}/leads`, {
          headers: authHeader, params: { limit: 500 },
        }),
      ]);
      setRows(queue.data);
      setLeads(funnel.data);
    } catch (err) {
      setRows([]);
      setLeads([]);
      if (err?.response?.status === 403) {
        setForbidden(true);
      } else {
        setError("Could not load the queue. Please try again.");
      }
    }
  }, [authHeader]);

  useEffect(() => {
    load();
  }, [load]);

  const counts = useMemo(() => {
    const tally = { IN_PROGRESS: 0, AWAITING_REVIEW: 0, RELEASED: 0 };
    (rows || []).forEach((r) => {
      if (tally[r.status] != null) tally[r.status] += 1;
    });
    return tally;
  }, [rows]);

  const leadCounts = useMemo(() => {
    const tally = { NOT_SIGNED_UP: 0, SIGNED_UP_NOT_STARTED: 0, STARTED: 0 };
    (leads || []).forEach((l) => {
      if (tally[l.status] != null) tally[l.status] += 1;
    });
    return tally;
  }, [leads]);

  const visibleLeads = useMemo(() => {
    const matches = buildMatcher(query);
    return (leads || [])
      .filter((l) => leadFilter === "ALL" || l.status === leadFilter)
      .filter((l) => matches(haystack([l.email, l.subject_name, l.subject_code])));
  }, [leads, leadFilter, query]);

  const visible = useMemo(() => {
    const matches = buildMatcher(query);
    return (rows || [])
      .filter((r) => filter === "ALL" || r.status === filter)
      .filter((r) =>
        matches(haystack([r.student_name, r.student_email, r.subject_name, r.subject_code]))
      );
  }, [rows, filter, query]);

  /**
   * Open a submission in the marking view.
   *
   * SolvePaper takes its paper from navigation state rather than the URL, so
   * the questions have to be fetched first — the same two steps the classroom
   * assignment list does (TeacherAssignmentListItem).
   */
  async function openForMarking(row) {
    setBusy(`mark:${row.user_paper_attempt_guid}`);
    try {
      const { data } = await axios.get(
        `${BASE_API_URL}/api/v1/user-paper-attempts/questions-with-attempt/${row.user_paper_attempt_guid}`,
        { headers: authHeader }
      );
      navigate("/my-papers/solve-paper", {
        state: {
          questions: data,
          paper_Guid: row.user_paper_attempt_guid,
          subject_name: row.subject_name,
          formattedPaperName: `Free paper — ${row.subject_name} (${row.subject_code})`,
          time_hour: 0,
          time_min: 0,
          paperMode: "REVIEW",
          viewerRole: "INSTRUCTOR",
          feedbackReleased: row.status === "RELEASED",
        },
      });
    } catch (err) {
      toast({
        title: "Could not open that paper.",
        description: err?.response?.data?.detail || "Please try again.",
        status: "error",
        duration: 6000,
      });
    } finally {
      setBusy(null);
    }
  }

  async function release(row) {
    setBusy(`release:${row.user_paper_attempt_guid}`);
    try {
      const { data } = await axios.post(
        `${ENDPOINT}/review/${row.user_paper_attempt_guid}/release`,
        {},
        { headers: authHeader }
      );
      toast({
        title: data.report_email_sent ? "Released" : "Released, but the email didn't send",
        description: data.report_email_sent
          ? `Breakdown emailed to ${data.student_email}.`
          : "Use Resend once mail is working again.",
        status: data.report_email_sent ? "success" : "warning",
        duration: 6000,
      });
      await load();
    } catch (err) {
      toast({
        title: "Could not release the feedback.",
        description: err?.response?.data?.detail || "Please try again.",
        status: "error",
        duration: 6000,
      });
    } finally {
      setBusy(null);
    }
  }

  /**
   * Send the breakdown email again.
   *
   * Release never sends a second email, so this is the only way to recover a
   * bounced or deleted one. Available on every released row, for as long as
   * the row exists.
   */
  async function resend(row) {
    setBusy(`resend:${row.user_paper_attempt_guid}`);
    try {
      const { data } = await axios.post(
        `${ENDPOINT}/review/${row.user_paper_attempt_guid}/resend`,
        {},
        { headers: authHeader }
      );
      toast({
        title: data.report_email_sent ? "Email sent again" : "The email didn't send",
        description: data.report_email_sent
          ? `Breakdown re-sent to ${data.student_email}.`
          : "Mail is not going out right now. Try again shortly.",
        status: data.report_email_sent ? "success" : "warning",
        duration: 6000,
      });
    } catch (err) {
      toast({
        title: "Could not send the email.",
        description: err?.response?.data?.detail || "Please try again.",
        status: "error",
        duration: 6000,
      });
    } finally {
      setBusy(null);
    }
  }

  const openReport = (row) =>
    navigate(`/classroom/report/${row.user_paper_attempt_guid}`, {
      state: { viewerRole: "INSTRUCTOR" },
    });

  /**
   * A 403 means the signed-in account is not Stande staff — not that there
   * is nothing to review. Showing the dashboard with every number at zero
   * would read as "the campaign has no submissions", which is a different
   * and wrong thing to tell someone, so the page says what is actually
   * wrong and offers the way out.
   */
  if (forbidden) {
    return (
      <Box className="stande-classroom" data-theme={isDark ? "dark" : "light"}
        minHeight="100vh" bg={c.page} transition="background 0.2s">
        <Box px={{ base: "20px", md: "75px" }}>
          <Header handleLogout={handleLogout} isDark={isDark}
            onToggleColorMode={toggleColorMode} />
        </Box>
        <Flex direction="column" align="center" justify="center" py="100px"
          px="20px" gap="12px" textAlign="center">
          <Flex boxSize="52px" borderRadius="full" bg={c.surfaceSubtle}
            align="center" justify="center">
            <Icon as={HiOutlineLockClosed} boxSize="22px" color={c.textMuted} />
          </Flex>
          <Text fontSize="19px" fontWeight="500" color={c.text}>
            You don&apos;t have access to this page
          </Text>
          <Text fontSize="14px" fontWeight="300" color={c.textSub} maxW="420px">
            Free paper submissions carry students&apos; contact details, so they
            are only visible to Stande staff accounts. If you should have
            access, ask someone on the team to grant it — then reload.
          </Text>
          <Button
            mt="6px" size="sm" h="34px" px="14px" borderRadius="8px"
            bg={c.solidBg} color={c.solidText} fontSize="13px" fontWeight="500"
            _hover={{ opacity: 0.88 }}
            onClick={() => navigate("/classroom/student/dashboard")}
          >
            Go to my dashboard
          </Button>
        </Flex>
      </Box>
    );
  }

  return (
    <Box className="stande-classroom" data-theme={isDark ? "dark" : "light"}
      minHeight="100vh" bg={c.page} transition="background 0.2s">
      <Box px={{ base: "20px", md: "75px" }}>
        <Header handleLogout={handleLogout} isDark={isDark}
          onToggleColorMode={toggleColorMode} />
      </Box>

      <Box px={{ base: "20px", md: "75px" }} py={{ base: "28px", md: "48px" }}>
        <Flex justify="space-between" align="flex-end" gap="16px" wrap="wrap" mb="30px">
          <VStack align="flex-start" spacing="4px">
            <Text fontSize="28px" fontWeight="500" color={c.text}>
              Free practice papers
            </Text>
            <Text fontSize="16px" color={c.textSub} fontWeight="300">
              Submissions from the free paper campaign. Releasing emails the
              student their full breakdown.
            </Text>
          </VStack>
          <Button
            leftIcon={<Icon as={HiOutlineArrowPath} boxSize="15px" />}
            size="sm" h="34px" borderRadius="8px" variant="outline"
            borderColor={c.border} color={c.textSub} bg="transparent"
            fontSize="13px" fontWeight="400"
            _hover={{ bg: c.hover, color: c.text }}
            isDisabled={rows === null}
            onClick={load}
          >
            Refresh
          </Button>
        </Flex>

        <VStack align="stretch" spacing="14px">
          <Card>
            <Flex gap="20px" wrap="wrap">
              <Tile
                label="Waiting on you"
                value={rows === null ? "—" : counts.AWAITING_REVIEW}
                tone={counts.AWAITING_REVIEW ? b.secondaryOrange : c.text}
              />
              <Tile label="Still working" value={rows === null ? "—" : counts.IN_PROGRESS} />
              <Tile label="Released" value={rows === null ? "—" : counts.RELEASED} />
              <Tile
                label="No account yet"
                value={leads === null ? "—" : leadCounts.NOT_SIGNED_UP}
                tone={leadCounts.NOT_SIGNED_UP ? b.secondaryOrange : c.text}
              />
              <Tile
                label="Paper requests"
                value={leads === null ? "—" : (leads || []).length}
                hint="all time"
              />
            </Flex>
          </Card>

          <Card
            title="Submissions"
            meta={
              <InputGroup size="sm" maxW="240px">
                <InputLeftElement pointerEvents="none" h="32px">
                  <Icon as={HiOutlineMagnifyingGlass} color={c.textMuted} boxSize="14px" />
                </InputLeftElement>
                <Input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Find a student or subject"
                  h="32px" borderRadius="8px" bg={c.inputBg} border="1px solid"
                  borderColor={c.inputBorder} color={c.text} fontSize="12.5px"
                  _placeholder={{ color: c.textMuted }}
                  _focus={{ borderColor: c.textMuted, boxShadow: "none" }}
                />
              </InputGroup>
            }
          >
            <HStack spacing="7px" wrap="wrap" mb="16px">
              {FILTERS.map((f) => {
                const active = filter === f.key;
                const n = f.key === "ALL" ? (rows || []).length : counts[f.key];
                return (
                  <Button
                    key={f.key}
                    size="sm" h="30px" px="13px" borderRadius="999px"
                    fontSize="12.5px" fontWeight="400"
                    bg={active ? c.solidBg : "transparent"}
                    color={active ? c.solidText : c.textSub}
                    border="1px solid"
                    borderColor={active ? c.solidBg : c.border}
                    _hover={active ? { opacity: 0.88 } : { bg: c.hover, color: c.text }}
                    onClick={() => setFilter(f.key)}
                  >
                    {f.label}
                    {rows !== null && (
                      <Text as="span" ml="6px"
                        color={active ? c.solidText : c.textMuted}
                        opacity={active ? 0.7 : 1}
                        sx={{ fontVariantNumeric: "tabular-nums" }}>
                        {n}
                      </Text>
                    )}
                  </Button>
                );
              })}
            </HStack>

            {rows === null && (
              <VStack align="stretch" spacing="8px">
                {[0, 1, 2].map((i) => (
                  <Skeleton key={i} h="52px" borderRadius="9px"
                    startColor={c.surfaceSubtle} endColor={c.hover} />
                ))}
              </VStack>
            )}

            {rows !== null && error && (
              <Empty title="Couldn't load this" body={error} />
            )}

            {rows !== null && !error && visible.length === 0 && (
              <Empty
                title={query ? "Nothing matches that" : "Nothing here right now"}
                body={
                  query
                    ? "Try a different name, email or subject."
                    : "Sign-ups from the campaign land here as soon as they start their paper."
                }
              />
            )}

            {rows !== null && !error && visible.length > 0 && (
              <VStack align="stretch" spacing="1px">
                {visible.map((row) => {
                  const guid = row.user_paper_attempt_guid;
                  const stage = STAGE_OF[row.status] || "not_submitted";
                  const released = row.status === "RELEASED";
                  const since = waitingFor(row.updated_at);
                  return (
                    <Flex
                      key={guid} align="center" gap="13px" px="10px" py="11px"
                      borderRadius="9px" transition="background 0.15s"
                      _hover={{ bg: c.hover }} wrap={{ base: "wrap", md: "nowrap" }}
                    >
                      <Box boxSize="8px" borderRadius="2px" flexShrink={0}
                        bg={STAGES[stage].dot} />

                      {/* Accounts created from the email link often have no
                          name yet, so the address is the only identity there
                          is — shown once, not twice. */}
                      <Box flex="1 1 190px" minW={0}>
                        <Text fontSize="13.5px" fontWeight="500" color={c.text} isTruncated>
                          {row.student_name || row.student_email}
                        </Text>
                        {row.student_name && (
                          <Text fontSize="11.5px" color={c.textMuted} isTruncated>
                            {row.student_email}
                          </Text>
                        )}
                      </Box>

                      <Box flex="0 1 170px" minW={0} display={{ base: "none", md: "block" }}>
                        <Text fontSize="13px" color={c.textSub} isTruncated>
                          {row.subject_name}
                        </Text>
                        <Text fontSize="11.5px" color={c.textMuted}>
                          {row.subject_code}
                        </Text>
                      </Box>

                      <Box flex="0 1 150px" minW={0}>
                        <Text fontSize="12.5px" color={c.textSub} isTruncated>
                          {STATUS_LABEL[row.status] || row.status}
                        </Text>
                        <Tooltip label={formatDateTime(row.updated_at) || ""}
                          placement="top" hasArrow fontSize="11px" isDisabled={!since}>
                          <Text fontSize="11.5px" color={c.textMuted} width="fit-content">
                            {since || "—"}
                          </Text>
                        </Tooltip>
                      </Box>

                      <Text flex="0 0 64px" textAlign="right" fontSize="14px"
                        display={{ base: "none", md: "block" }}
                        fontWeight={row.total_scored_marks == null ? "300" : "600"}
                        color={row.total_scored_marks == null ? c.textMuted : c.text}
                        sx={{ fontVariantNumeric: "tabular-nums" }}>
                        {row.total_scored_marks == null ? "—" : row.total_scored_marks}
                      </Text>

                      <HStack spacing="5px" flex="0 0 auto" ml="auto">
                        {row.submitted && (
                          <RowAction
                            label={released ? "View submission" : "Mark submission"}
                            icon={HiOutlineArrowTopRightOnSquare}
                            isLoading={busy === `mark:${guid}`}
                            onClick={() => openForMarking(row)}
                          />
                        )}
                        <RowAction
                          label={row.has_report ? "Open report" : "No report built yet"}
                          icon={HiOutlineChartBar}
                          isDisabled={!row.has_report}
                          onClick={() => openReport(row)}
                        />
                        {released && (
                          <RowAction
                            label="Resend the breakdown email"
                            icon={HiOutlineEnvelope}
                            isLoading={busy === `resend:${guid}`}
                            onClick={() => resend(row)}
                          />
                        )}
                        {row.status === "AWAITING_REVIEW" && (
                          <Button
                            size="sm" h="30px" px="12px" borderRadius="7px"
                            fontSize="12.5px" fontWeight="500"
                            bg={c.solidBg} color={c.solidText}
                            _hover={{ opacity: 0.88 }}
                            isLoading={busy === `release:${guid}`}
                            onClick={() => release(row)}
                          >
                            Release &amp; email
                          </Button>
                        )}
                      </HStack>
                    </Flex>
                  );
                })}
              </VStack>
            )}
          </Card>

          {/* Everyone who asked for a paper, including those the review queue
              cannot show: it only knows about people who created an account
              and opened one. */}
          <Card
            title="Paper requests"
            meta={
              <Text fontSize="11.5px" color={c.textMuted}>
                From the landing page form
              </Text>
            }
          >
            <HStack spacing="7px" wrap="wrap" mb="16px">
              {LEAD_FILTERS.map((f) => {
                const active = leadFilter === f.key;
                const n = f.key === "ALL" ? (leads || []).length : leadCounts[f.key];
                return (
                  <Button
                    key={f.key}
                    size="sm" h="30px" px="13px" borderRadius="999px"
                    fontSize="12.5px" fontWeight="400"
                    bg={active ? c.solidBg : "transparent"}
                    color={active ? c.solidText : c.textSub}
                    border="1px solid"
                    borderColor={active ? c.solidBg : c.border}
                    _hover={active ? { opacity: 0.88 } : { bg: c.hover, color: c.text }}
                    onClick={() => setLeadFilter(f.key)}
                  >
                    {f.label}
                    {leads !== null && (
                      <Text as="span" ml="6px"
                        color={active ? c.solidText : c.textMuted}
                        opacity={active ? 0.7 : 1}
                        sx={{ fontVariantNumeric: "tabular-nums" }}>
                        {n}
                      </Text>
                    )}
                  </Button>
                );
              })}
            </HStack>

            {leads === null && (
              <VStack align="stretch" spacing="8px">
                {[0, 1].map((i) => (
                  <Skeleton key={i} h="44px" borderRadius="9px"
                    startColor={c.surfaceSubtle} endColor={c.hover} />
                ))}
              </VStack>
            )}

            {leads !== null && !error && visibleLeads.length === 0 && (
              <Empty
                title={query ? "Nothing matches that" : "Nobody here"}
                body={
                  query
                    ? "Try a different email or subject."
                    : leadFilter === "NOT_SIGNED_UP"
                    ? "Everyone who asked for a paper has created an account."
                    : "Requests from the landing page form show up here."
                }
              />
            )}

            {leads !== null && !error && visibleLeads.length > 0 && (
              <VStack align="stretch" spacing="1px">
                {visibleLeads.map((lead) => (
                  <Flex
                    key={lead.guid} align="center" gap="13px" px="10px" py="11px"
                    borderRadius="9px" transition="background 0.15s"
                    _hover={{ bg: c.hover }} wrap={{ base: "wrap", md: "nowrap" }}
                  >
                    <Box boxSize="8px" borderRadius="2px" flexShrink={0}
                      bg={lead.status === "NOT_SIGNED_UP"
                        ? b.secondaryOrange
                        : STAGES.released.dot} />

                    <Box flex="1 1 200px" minW={0}>
                      <Text fontSize="13.5px" fontWeight="500" color={c.text} isTruncated>
                        {lead.email}
                      </Text>
                      <Text fontSize="11.5px" color={c.textMuted} isTruncated>
                        {LEAD_LABEL[lead.status] || lead.status}
                        {lead.paper_email_sent ? "" : " · paper email never sent"}
                      </Text>
                    </Box>

                    <Box flex="0 1 170px" minW={0} display={{ base: "none", md: "block" }}>
                      <Text fontSize="13px" color={c.textSub} isTruncated>
                        {lead.subject_name}
                      </Text>
                      <Text fontSize="11.5px" color={c.textMuted}>
                        {lead.subject_code}
                      </Text>
                    </Box>

                    <Tooltip label={formatDateTime(lead.created_date) || ""}
                      placement="top" hasArrow fontSize="11px">
                      <Text flex="0 0 auto" ml="auto" fontSize="12px"
                        color={c.textMuted} whiteSpace="nowrap">
                        {waitingFor(lead.created_date) || "—"}
                      </Text>
                    </Tooltip>
                  </Flex>
                ))}
              </VStack>
            )}
          </Card>
        </VStack>
      </Box>
    </Box>
  );
}
